import { useCallback, useMemo } from "react";
import {
  normalizeSamplerPreset,
  type InstrumentSlot,
  type SamplerPreset,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { projectStore } from "../store/projectStore";

/**
 * Shared dock + Author write path for sampler presets.
 * Always goes through `updateInstrumentSamplerPreset` → SetInstrumentCommand.
 */
export function useSamplerPreset(slot: InstrumentSlot) {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const instrument = instruments[slot];
  const isSampler = instrument?.kind === "sampler";
  const preset = useMemo(
    () => (isSampler ? normalizeSamplerPreset(instrument.preset) : null),
    [isSampler, instrument],
  );

  const commit = useCallback((next: SamplerPreset) => {
    getLabActions()?.updateInstrumentSamplerPreset?.(slot, normalizeSamplerPreset(next));
  }, [slot]);

  const patch = useCallback((partial: Partial<SamplerPreset>) => {
    if (!preset) {
      return;
    }
    commit({ ...preset, ...partial });
  }, [commit, preset]);

  return { instrument, isSampler, preset, commit, patch };
}
